import { useEffect, useState } from "react";

/**
 * Something loaded once and shared: a route's page module, a data chunk.
 *
 * Why not React.lazy / use(): a component that suspends commits its Suspense fallback, and React 19 then holds the
 * content back until 300 ms after that fallback (react-dom 19.2: `globalMostRecentFallbackTime + 300 - now()`). On a
 * first load that was most of the wait: the signed-out hero's chunks were in at 442 ms and it showed at 670, the main
 * thread idle between (speed audit 2026-10-20, #341). With a loader the caller renders its own placeholder as a plain
 * element, so no fallback ever commits.
 */
export interface Loader<T> {
  /** Starts the load, once: later calls share it. A failure is forgotten, so the next call tries again. */
  readonly load: () => Promise<T>;
  /** The value, once loaded. */
  readonly value: () => T | undefined;
}

export function loader<T>(start: () => Promise<T>): Loader<T> {
  let value: T | undefined;
  let pending: Promise<T> | null = null;
  const load = (): Promise<T> => {
    if (!pending) {
      const p: Promise<T> = start().then(
        (v) => { value = v; return v; },
        (e: unknown) => { pending = null; throw e; },
      );
      // A preload nobody awaits must not report an unhandled rejection; whoever awaits still sees the error.
      p.catch(() => {});
      pending = p;
    }
    return pending;
  };
  return { load, value: () => value };
}

type Loaded<T> = { readonly value: T } | { readonly error: unknown } | null;

/**
 * The loader's value, or null until it's in (render a placeholder meanwhile). Starts the load while rendering, as
 * React.lazy does, when `wanted`. A failed load throws to the nearest error boundary, as a lazy component's would;
 * the boundary's retry remounts this, which loads again.
 */
export function useLoaded<T>(l: Loader<T>, wanted = true): T | null {
  const [state, setState] = useState<Loaded<T>>(() => { const v = l.value(); return v === undefined ? null : { value: v }; });
  if (wanted && !state) void l.load();
  useEffect(() => {
    if (!wanted || state) return;
    let live = true;
    l.load().then((value) => { if (live) setState({ value }); }, (error: unknown) => { if (live) setState({ error }); });
    return () => { live = false; };
  }, [l, wanted, state]);
  if (state && "error" in state) throw state.error;
  return state ? state.value : null;
}
